import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { useAuth } from './vmAuthenticate';  // Add this import
import { lambdaURL } from "../config";
import { sendRequest } from "./vmUtils";

// Sound imports
import panel_slide from '../sounds/receiver_up.mp3';
import panel_slide_reverse from '../sounds/receiver_down.mp3';

const PanelNavigationContext = createContext();

export const usePanelNavigation = () => {
  const context = useContext(PanelNavigationContext);
  if (!context) {
    throw new Error('usePanelNavigation must be used within a SlidingPanelSystem');
  }
  return context;
};


/**
 * A component that manages a stack of sliding panels for navigation with back/forward functionality
 * @param {Object} props
 * @param {React.ReactNode} props.children - Not used directly, maintained for React conventions
 * @param {React.ReactNode} props.initialPanel - The first panel to show
 */
const SlidingPanelSystem = ({ children, initialPanel }) => {
  const [panelStack, setPanelStack] = useState([initialPanel]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [animatingPanel, setAnimatingPanel] = useState(null);
  const [animationStage, setAnimationStage] = useState(null);
  const [isBackNavigation, setIsBackNavigation] = useState(false);
  const [refreshCallbacks, setRefreshCallbacks] = useState({});
  const [voteCount, setVoteCount] = useState("0");

  const { user } = useAuth();
  const userId = user?.userId;

  useEffect(() => {
    if (userId) {
      const fetchVoteCount = async () => {
        console.log("Fetching vote count for user:", userId);
        try {
          let jsonData = {
            command: "getUserProposalVotesCount",
          };
          sendRequest(lambdaURL, jsonData)
          .then(data => {
            if (data.body) {
              const parsedData = JSON.parse(data.body);
              console.log("New vote count: ", parsedData.count);
              setVoteCount(parsedData.count?.toString().padStart(5, '0'));
            }
          });
        } catch (error) {
          console.error('Error fetching vote count:', error);
        }
      };
      fetchVoteCount();
    }
  }, [userId]);

  // Sound effects
  const panel_sound = new Audio(panel_slide);
  panel_sound.volume = 0.75;
  const panel_sound_reverse = new Audio(panel_slide_reverse);
  panel_sound_reverse.volume = 0.75;

  const registerRefreshCallback = useCallback((panelIndex, callback) => {
    setRefreshCallbacks(prev => ({
      ...prev,
      [panelIndex]: callback
    }));
  }, []);

  const goBackWithRefresh = useCallback(() => {
    if (currentIndex <= 0) return;

    // If there's a refresh callback for the panel we're going back to, queue it
    const targetIndex = currentIndex - 1;
    const shouldRefresh = refreshCallbacks[targetIndex];

    setIsBackNavigation(true);
    setAnimatingPanel(currentIndex);
    setAnimationStage("outgoing");

    panel_sound_reverse.currentTime = 0;
    panel_sound_reverse.play().catch(error => {
      console.log('Sound playback failed:', error);
    });

    setTimeout(() => {
      setCurrentIndex(prev => prev - 1);
      setAnimatingPanel(targetIndex);
      setAnimationStage("incoming");

      // Call the refresh callback after the animation starts
      if (shouldRefresh) {
        shouldRefresh();
      }

      setTimeout(() => {
        setAnimationStage(null);
        setAnimatingPanel(null);
        setIsBackNavigation(false);
      }, 350);
    }, 350);
  }, [currentIndex, refreshCallbacks]);

  const pushPanel = (panel) => {
    setIsBackNavigation(false);
    setAnimatingPanel(currentIndex);
    setAnimationStage("outgoing");

    panel_sound.currentTime = 0;
    panel_sound.play().catch(error => {
      console.log('Sound playback failed:', error);
    });

    setTimeout(() => {
      // Remove any forward history when pushing a new panel
      setPanelStack(prev => [...prev.slice(0, currentIndex + 1), panel]);
      setCurrentIndex(prev => prev + 1);
      setAnimatingPanel(currentIndex + 1);
      setAnimationStage("incoming");
      setTimeout(() => {
        setAnimationStage(null);
        setAnimatingPanel(null);
      }, 350);
    }, 350);
  };

  const goBack = () => {
    if (currentIndex <= 0) return;
    
    setIsBackNavigation(true);
    setAnimatingPanel(currentIndex);
    setAnimationStage("outgoing");

    panel_sound_reverse.currentTime = 0;
    panel_sound_reverse.play().catch(error => {
      console.log('Sound playback failed:', error);
    });

    setTimeout(() => {
      setCurrentIndex(prev => prev - 1);
      setAnimatingPanel(currentIndex - 1);
      setAnimationStage("incoming");
      setTimeout(() => {
        setAnimationStage(null);
        setAnimatingPanel(null);
        setIsBackNavigation(false);
      }, 350);
    }, 350);
  };

  const goForward = () => {
    if (currentIndex >= panelStack.length - 1) return;
    
    setIsBackNavigation(false);
    setAnimatingPanel(currentIndex);
    setAnimationStage("outgoing");

    panel_sound.currentTime = 0;
    panel_sound.play().catch(error => {
      console.log('Sound playback failed:', error);
    });

    setTimeout(() => {
      setCurrentIndex(prev => prev + 1);
      setAnimatingPanel(currentIndex + 1);
      setAnimationStage("incoming");
      setTimeout(() => {
        setAnimationStage(null);
        setAnimatingPanel(null);
      }, 350);
    }, 350);
  };

  const getPanelStyle = (index) => {
    const isAnimating = animatingPanel === index;
    const isCurrentPanel = index === currentIndex;
    
    const baseStyles = {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      transition: isAnimating ? "transform 350ms ease-in-out" : "none",
      paddingTop: "60px"
    };

    if (!isCurrentPanel && !isAnimating && index !== currentIndex) {
      return {
        ...baseStyles,
        transform: "translateX(-200%)",
        zIndex: -1,
      };
    }

    if (isAnimating) {
      if (animationStage === "outgoing") {
        const exitDirection = isBackNavigation ? "100%" : "-100%";
        return {
          ...baseStyles,
          transform: `translateX(${exitDirection})`,
          zIndex: index,
        };
      } else if (animationStage === "incoming") {
        return {
          ...baseStyles,
          transform: "translateX(0)",
          zIndex: index + 1,
        };
      }
    }

    if (isCurrentPanel && !animationStage) {
      return {
        ...baseStyles,
        transform: "translateX(0)",
        zIndex: panelStack.length,
      };
    }

    const waitingPosition = isBackNavigation ? "-100%" : "100%";
    return {
      ...baseStyles,
      transform: `translateX(${waitingPosition})`,
      zIndex: index,
    };
  };

  const resetToInitialPanel = (newInitialPanel) => {
    setPanelStack([newInitialPanel]);
    setCurrentIndex(0);
    setAnimatingPanel(null);
    setAnimationStage(null);
  };

  const getNavigationButtonStyle = (isActive) => ({
    position: 'absolute',
    top: '10px',
    width: '40px',
    height: '40px',
    zIndex: 50,
    borderRadius: '8px',
    border: 'none',
    cursor: isActive ? 'pointer' : 'default',
    backgroundColor: isActive ? '#2ecc71' : '#95a5a6',
    color: '#ffffff',
    fontSize: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0
  });

  //const canGoBack = currentIndex > 0;
  //const canGoForward = currentIndex < panelStack.length - 1;

  const refreshVoteCount = useCallback(async () => {
    console.log("Calling refreshVoteCount for user: ", userId);
    if (userId) {
      try {
        let jsonData = {
          command: "getUserProposalVotesCount",
        };
        sendRequest(lambdaURL, jsonData)
        .then(data => {
          if (data.body) {
            const parsedData = JSON.parse(data.body);
            console.log("refreshVoteCount new count: ", parsedData.count);
            setVoteCount(parsedData.count?.toString().padStart(5, '0'));
          }
        });
      } catch (error) {
        console.error('Error refreshing vote count:', error);
      }
    }
  }, [userId]);

  const navigationValue = {
    goBack: goBackWithRefresh,
    goForward,
    pushPanel,
    registerRefreshCallback,
    resetToInitialPanel,
    refreshVoteCount,
    canGoBack: currentIndex > 0,
    canGoForward: currentIndex < panelStack.length - 1,
    currentIndex
  };

  return (
      <PanelNavigationContext.Provider value={navigationValue}>
      <div className="vm-section-container">
        <button
          onClick={goBackWithRefresh}
          disabled={!navigationValue.canGoBack}
          style={{
            ...getNavigationButtonStyle(navigationValue.canGoBack),
            left: '10px'
          }}
        >
          ←
        </button>
        {userId && (
          <div style={{
            position: 'absolute',
            top: '10px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 'calc(100% - 140px)',
            display: 'flex',
            flexDirection: 'column',
            gap: '2px',
          }}>
            <div style={{
              padding: '2px 2px',
              fontSize: '14px',
              color: '#444',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid #ddd',
              borderRadius: '4px 4px 0 0',
              width: '100%',
              textAlign: 'center',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis'
            }}>
              <span >{userId}</span>
            </div>
            <div style={{
              padding: '2px 2px',
              fontSize: '14px',
              color: '#666',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid #ddd',
              borderRadius: '0 0 4px 4px',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px'
            }}>
              <span className="font-medium">VOTES: </span>
              <span style={{
                backgroundColor: '#555',
                color: '#e0e0e0',
                padding: '1px 4px',
                borderRadius: '3px',
                fontFamily: 'monospace',
                fontSize: '15px',
                letterSpacing: '1px',
                fontWeight: '500',
                minWidth: '24px',
                textAlign: 'right'
              }}>{voteCount}</span>
            </div>
          </div>
        )}
        <button
          onClick={goForward}
          disabled={!navigationValue.canGoForward}
          style={{
            ...getNavigationButtonStyle(navigationValue.canGoForward),
            right: '10px'
          }}
        >
          →
        </button>
        {panelStack.map((panel, index) => (
          <div
            key={index}
            className="vm-sliding-panel"
            style={getPanelStyle(index)}
          >
            {React.cloneElement(panel, {
              onNavigate: pushPanel,
              panelIndex: index
            })}
          </div>
        ))}
      </div>
    </PanelNavigationContext.Provider>
  );
};

export { SlidingPanelSystem };
